import { useNavigate } from "react-router-dom";

import { useUserContext } from "../contexts/UserContext";
import { Roles } from "../models/autogenerated-deliveryService-gql/graphql";
import { RoutePath } from "../models/routePath";
import { TagName } from "../models/tag";
import { splitRole } from "../utils/roleNormalization";

const useNavigateTo = () => {
  const navigate = useNavigate();
  const { user } = useUserContext();

  const navigateWithRefresh = (to: string) =>
    navigate(to, { state: { refreshAt: Date.now() } });

  const getEffectiveActiveRole = (): Roles | null => {
    if (!user.role) return null;

    return splitRole(user.role)[0] || null;
  };

  const getDeliveriesRouteForRole = (): RoutePath => {
    const effectiveRole = getEffectiveActiveRole();

    if (effectiveRole === Roles.Customer) {
      return RoutePath.OutboundDeliveries;
    }

    return RoutePath.InboundDeliveries;
  };

  const getNewDeliveryRouteForRole = (): RoutePath => {
    const effectiveRole = getEffectiveActiveRole();

    if (effectiveRole === Roles.Customer) {
      return RoutePath.NewOutboundDelivery;
    }

    return RoutePath.NewOrder;
  };

  const navigateBack = () => navigate(-1);
  const navigateToDashboard = () => navigateWithRefresh(RoutePath.Home);
  const navigateToNewOrder = () =>
    navigateWithRefresh(getNewDeliveryRouteForRole());
  const navigateToDeliveries = () =>
    navigateWithRefresh(getDeliveriesRouteForRole());
  const navigateToDeliveriesWithTag = (tag: TagName) =>
    getDeliveriesRouteForRole() === RoutePath.OutboundDeliveries
      ? navigateWithRefresh(RoutePath.OutboundDeliveries)
      : navigateWithRefresh(`${RoutePath.InboundDeliveries}?tag=${tag}`);
  const navigateToDeliveryDetails = (id: string) =>
    navigateWithRefresh(`${RoutePath.InboundDelivery}/${id}`);
  const navigateToEditOrder = (id: string) =>
    navigateWithRefresh(`${RoutePath.EditOrder}/${id}`);
  const navigateToSplitOrder = (id: string) =>
    navigateWithRefresh(`${RoutePath.SplitOrder}/${id}`);
  const navigateToConfirmQuantities = (id: string) =>
    navigateWithRefresh(`${RoutePath.ConfirmQuantities}/${id}`);
  const navigateToConfirmDate = (id: string) =>
    navigateWithRefresh(`${RoutePath.ConfirmDeliveryDate}/${id}`);
  const navigateToBookTimeSlot = (id: string) =>
    navigateWithRefresh(`${RoutePath.BookTimeSlot}/${id}`);

  return {
    navigateBack,
    navigateToDashboard,
    navigateToNewOrder,
    navigateToDeliveries,
    navigateToDeliveryDetails,
    navigateToEditOrder,
    navigateToSplitOrder,
    navigateToConfirmQuantities,
    navigateToConfirmDate,
    navigateToBookTimeSlot,
    navigateToDeliveriesWithTag,
  };
};

export default useNavigateTo;
